import { BitacoraPriceData } from '../../api/market_comments';
import { COLORS } from '../../utils/colors';
import {
  getChartThemeColors,
  tooltipContainerStyle,
} from '../../utils/chartTheme';

/**
 * Puntos de Bitácora (precios informados desde el mercado) sobre los
 * gráficos de dispersión. Compartido por DailyMarketChart y VarietyPriceChart
 * para que los dos agrupen, ponderen y muestren el hover igual.
 */

export type BitacoraMode = 'average' | 'client' | 'label';

/** Morados distinguibles entre sí, para una serie por cliente o etiqueta. */
export const BITACORA_PALETTE = [
  COLORS.bitacora,
  '#C084FC',
  '#6B21A8',
  '#E879F9',
  '#7C3AED',
  '#D8B4FE',
  '#581C87',
  '#A855F7',
];

const MAX_TOOLTIP_ITEMS = 3;

export interface BitacoraPoint {
  x: string;
  y: number | null;
  isBitacora?: true;
  bitacoraGroup?: string | null;
  // Por qué se separó el punto; en "average" no viene.
  bitacoraGroupBy?: 'client' | 'label';
  bitacoraColor?: string;
  bitacoraRecords?: number;
  bitacoraBultos?: number;
  bitacoraItems?: BitacoraPriceData[];
}

export interface BitacoraSeries {
  name: string;
  type: 'scatter';
  data: BitacoraPoint[];
  color: string;
  isBitacoraSeries: true;
}

/** Para encontrar las series de Bitácora entre las demás del gráfico. */
export const isBitacoraSeries = (series: unknown): series is BitacoraSeries =>
  typeof series === 'object' &&
  series !== null &&
  (series as BitacoraSeries).isBitacoraSeries === true;

const boxesOf = (item: BitacoraPriceData) => item.unit_count || 1;

/**
 * Promedio ponderado por cajas (unit_count): un precio que cubre 300 cajas
 * del contenedor pesa más que uno de 10. En los registros desde contenedor,
 * unit_count son las cajas del producto; en los manuales, 1.
 */
export const weightedBitacoraPrice = (items: BitacoraPriceData[]) => {
  const totalBoxes = items.reduce((sum, item) => sum + boxesOf(item), 0);
  return (
    items.reduce((sum, item) => sum + item.price_kg * boxesOf(item), 0) /
    totalBoxes
  );
};

/**
 * Arma las series de Bitácora para las categorías del eje X (calibres o
 * variedades). En modo "average" hay una sola serie; en "client" y "label",
 * una por cliente o por etiqueta, ordenadas de más a menos cajas.
 */
export function buildBitacoraSeries({
  records,
  categories,
  categoryOf,
  mode,
  label,
  noClientLabel,
  noLabelLabel,
}: {
  records: BitacoraPriceData[];
  categories: string[];
  /** Categoría del registro, en mayúsculas; null si no aplica al gráfico. */
  categoryOf: (record: BitacoraPriceData) => string | null;
  mode: BitacoraMode;
  label: string;
  noClientLabel: string;
  noLabelLabel: string;
}): BitacoraSeries[] {
  if (records.length === 0) return [];

  // Las etiquetas se escriben a mano: "Humboldt", "HUMBOLDT" y "Humboldt "
  // son la misma. El grupo sin etiqueta usa la misma forma, para que una
  // etiqueta escrita igual que ese nombre no dé dos series con el mismo título.
  // Un registro desde contenedor queda sin etiqueta si a ese precio había
  // cajas de varias etiquetas, por eso el grupo se llama "sin etiqueta o varias".
  const normalizeLabel = (value: string) =>
    value.trim().replace(/\s+/g, ' ').toUpperCase();
  const groupOf = (record: BitacoraPriceData) => {
    if (mode === 'client') return record.seller?.trim() || noClientLabel;
    if (mode === 'label')
      return normalizeLabel(record.label || '') || normalizeLabel(noLabelLabel);
    return null;
  };
  const groupBy = mode === 'average' ? undefined : mode;

  const groups = new Map<string | null, BitacoraPriceData[]>();
  for (const record of records) {
    const key = groupOf(record);
    groups.set(key, [...(groups.get(key) || []), record]);
  }

  const built = Array.from(groups.entries())
    .map(([group, items]) => {
      const data: BitacoraPoint[] = categories.map((category) => {
        const matching = items.filter(
          (item) => categoryOf(item) === category.toUpperCase()
        );
        if (matching.length === 0) return { x: category, y: null };
        return {
          x: category,
          y: weightedBitacoraPrice(matching),
          isBitacora: true,
          bitacoraGroup: group,
          bitacoraGroupBy: groupBy,
          bitacoraRecords: matching.length,
          bitacoraBultos: matching.reduce((sum, item) => sum + boxesOf(item), 0),
          bitacoraItems: matching,
        };
      });
      const boxes = items.reduce((sum, item) => sum + boxesOf(item), 0);
      return { group, data, boxes };
    })
    .filter(({ data }) => data.some((point) => point.y !== null))
    .sort((a, b) => b.boxes - a.boxes);

  return built.map(({ group, data }, index) => {
    const color = BITACORA_PALETTE[index % BITACORA_PALETTE.length];
    return {
      name: (group ? `${label} · ${group}` : label).toUpperCase(),
      type: 'scatter',
      data: data.map((point) =>
        point.isBitacora ? { ...point, bitacoraColor: color } : point
      ),
      color,
      isBitacoraSeries: true,
    };
  });
}

// El comentario lo escribe el usuario y el tooltip se inyecta como HTML.
const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const truncate = (value: string, max: number) =>
  value.length > max ? `${value.slice(0, max - 1)}…` : value;

// Más recientes primero; en el mismo día, los que traen foto y después los
// de más cajas, para que la foto no quede escondida en "+N más".
const sortBitacoraItems = (items: BitacoraPriceData[]) =>
  [...items].sort(
    (a, b) =>
      b.date_from.localeCompare(a.date_from) ||
      Number(!!b.photo_url) - Number(!!a.photo_url) ||
      boxesOf(b) - boxesOf(a)
  );

/** Un registro dentro del hover: cliente (o contenedor), días desde la ETA, precio, comentario, foto y autor. */
function bitacoraItemHtml(
  item: BitacoraPriceData,
  {
    showSeller,
    currency,
    unit,
    darkMode,
    t,
  }: {
    showSeller: boolean;
    currency: string;
    unit: string;
    darkMode: boolean;
    t: (key: string) => string;
  }
) {
  const colors = getChartThemeColors(darkMode);
  const meta = [item.author, item.date_from].filter(Boolean).join(' · ');
  const who = showSeller ? item.seller : null;
  return `
      <div style="display: flex; gap: 8px; padding-top: 8px; margin-top: 8px; border-top: 1px solid ${colors.tooltipBorder};">
        ${
          item.photo_url
            ? `<img src="${escapeHtml(item.photo_url)}" style="width: 48px; height: 48px; object-fit: cover; border-radius: 6px; flex-shrink: 0;" />`
            : ''
        }
        <div style="min-width: 0; flex: 1;">
          <div style="display: flex; justify-content: space-between; gap: 8px; font-size: 12px;">
            <span style="color: ${colors.tooltipValue}; font-weight: 600;">
              ${escapeHtml(truncate(who || item.container_number || t('Sin contenedor'), 28))}
            </span>
            <span style="color: ${colors.tooltipValue}; white-space: nowrap;">${item.price_kg.toFixed(2)} ${currency}${unit}</span>
          </div>
          ${
            item.days_from_eta != null
              ? `<div style="font-size: 11px; color: ${colors.tooltipText};">${item.days_from_eta} ${item.days_from_eta === 1 ? t('día') : t('días')} ${t('desde la ETA')}</div>`
              : ''
          }
          ${
            item.content
              ? `<div style="font-size: 12px; color: ${colors.tooltipTitle}; margin-top: 2px; white-space: normal;">${escapeHtml(truncate(item.content, 110))}</div>`
              : ''
          }
          ${meta ? `<div style="font-size: 11px; color: ${colors.tooltipText}; margin-top: 2px;">${escapeHtml(meta)}</div>` : ''}
        </div>
      </div>`;
}

/**
 * Los gráficos de Evolución usan tooltip compartido: lista todas las series
 * de esa fecha. En vez de reemplazarlo, se le agrega al final el detalle de
 * los registros de Bitácora de esa fecha.
 */
export function appendBitacoraDetails(
  html: string,
  {
    w,
    x,
    currency,
    unit,
    darkMode,
    t,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- globals de ApexCharts
    w: any;
    x: unknown;
    currency: string;
    unit: string;
    darkMode: boolean;
    t: (key: string) => string;
  }
) {
  // ApexCharts descarta los campos propios de la serie (isBitacoraSeries)
  // pero conserva los de cada punto, así que se busca por punto.
  const items = sortBitacoraItems(
    (w?.globals?.initialSeries || []).flatMap(
      (series: { data?: BitacoraPoint[] }) =>
        (series.data || [])
          .filter((point) => point?.isBitacora && point.x === x)
          .flatMap((point) => point.bitacoraItems || [])
    )
  );
  if (items.length === 0) return html;

  const colors = getChartThemeColors(darkMode);
  const shown = items.slice(0, MAX_TOOLTIP_ITEMS);
  return `${html}
    <div style="order: 9999; padding: 4px 10px 8px; max-width: 320px; white-space: normal; border-top: 1px solid ${colors.tooltipBorder};">
      <div style="font-size: 12px; font-weight: bold; color: ${COLORS.bitacora}; margin-top: 6px;">
        ${t('Bitácora')} · ${items.length} ${t('registros')}
      </div>
      ${shown
        .map((item) =>
          bitacoraItemHtml(item, { showSeller: true, currency, unit, darkMode, t })
        )
        .join('')}
      ${
        items.length > shown.length
          ? `<div style="font-size: 11px; color: ${colors.tooltipText}; text-align: center; margin-top: 6px;">+${items.length - shown.length} ${t('más')}</div>`
          : ''
      }
    </div>`;
}

/**
 * Hover de un punto de Bitácora: precio ponderado y los registros que lo
 * forman, con cliente, días desde la ETA, comentario, foto y quién lo registró.
 */
export function renderBitacoraTooltip({
  point,
  categoryLabel,
  currency,
  unit,
  darkMode,
  t,
}: {
  point: BitacoraPoint;
  categoryLabel: string;
  currency: string;
  unit: string;
  darkMode: boolean;
  t: (key: string) => string;
}) {
  const colors = getChartThemeColors(darkMode);
  const color = point.bitacoraColor || COLORS.bitacora;
  const items = sortBitacoraItems(point.bitacoraItems || []);
  const shown = items.slice(0, MAX_TOOLTIP_ITEMS);
  const title = point.bitacoraGroup
    ? `${t('Bitácora')} · ${escapeHtml(point.bitacoraGroup)}`
    : t('Bitácora');
  const itemHtml = (item: BitacoraPriceData) =>
    bitacoraItemHtml(item, {
      // El cliente ya está en el título si el punto es de un cliente.
      showSeller: point.bitacoraGroupBy !== 'client',
      currency,
      unit,
      darkMode,
      t,
    });


  return `
    <div style="${tooltipContainerStyle(darkMode)} border-left: 4px solid ${color}; max-width: 320px; white-space: normal;">
      <div style="text-align: center; font-size: 15px; font-weight: bold; color: ${color}; margin-bottom: 2px;">
        ${title}
      </div>
      <div style="text-align: center; font-size: 13px; color: ${colors.tooltipText}; margin-bottom: 8px;">
        ${escapeHtml(categoryLabel)}
      </div>
      ${
        point.y !== null
          ? `
        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
          <span style="color: ${colors.tooltipText};">${t('Precio')}:</span>
          <span style="font-weight: bold; color: ${colors.tooltipValue}; margin-left: 10px;">${point.y.toFixed(2)} ${currency}${unit}</span>
        </div>
        <div style="font-size: 11px; color: ${colors.tooltipText}; text-align: center;">
          ${point.bitacoraRecords} ${t('registros')} · ${t('promedio ponderado')}
        </div>
        ${shown.map(itemHtml).join('')}
        ${
          items.length > shown.length
            ? `<div style="font-size: 11px; color: ${colors.tooltipText}; text-align: center; margin-top: 6px;">+${items.length - shown.length} ${t('más')}</div>`
            : ''
        }`
          : `<div style="font-size: 12px; color: ${colors.tooltipText}; text-align: center;">${t('Sin datos')}</div>`
      }
    </div>`;
}
